import React, { useState, useEffect } from 'react';
import Navbar from '../common/Navbar';
import Card from '../common/Card';
import Button from '../common/Button';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import './AthleteDashboard.css';

const AthleteDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayWorkout, setTodayWorkout] = useState(null);
  const [team, setTeam] = useState(null);
  const [athleteName, setAthleteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completedExercises] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, dashboardRes] = await Promise.all([
        api.get('/athlete/stats'),
        api.get('/athlete/dashboard')
      ]);

      // Transform maxes array to oneRepMaxes object for compatibility
      const maxesArray = statsRes.data.maxes || [];
      const oneRepMaxes = {};
      maxesArray.forEach(max => {
        if (max.exerciseName && max.oneRepMax) {
          oneRepMaxes[max.exerciseName] = max.oneRepMax;
        }
      });

      setStats({
        ...statsRes.data,
        oneRepMaxes
      });
      setTodayWorkout(dashboardRes.data.todayWorkout);
      setTeam(dashboardRes.data.team);
      setAthleteName(dashboardRes.data.athlete?.firstName || '');
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateWeight = (exercise) => {
    if (exercise.weight) {
      return exercise.weight;
    }
    if (exercise.percentage && stats?.oneRepMaxes) {
      const oneRM = stats.oneRepMaxes[exercise.exerciseId];
      if (oneRM) {
        return Math.round(oneRM * (exercise.percentage / 100));
      }
    }
    return null;
  };


  if (loading) {
    return (
      <div className="athlete-dashboard">
        <Navbar />
        <div className="dashboard-content">
          <div className="loading">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="athlete-dashboard">
      <Navbar />
      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1>Welcome back, {athleteName || user?.firstName || 'Athlete'}!</h1>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {/* Team Badge */}
        {team ? (
          <div className="team-badge">
            <span className="team-badge-icon">👥</span>
            <span className="team-badge-name">{team.teamName}</span>
          </div>
        ) : (
          <div className="team-badge no-team">
            <span className="team-badge-icon">⚠️</span>
            <span className="team-badge-name">No Team</span>
          </div>
        )}

        <div className="dashboard-grid">
          {/* Today's Workout - Top */}
          <section className="workout-section">
            <Card>
              <div className="section-header">
                <h2>Today's Workout</h2>
                <Button variant="outline" size="sm" href="/athlete/workouts">
                  View Schedule
                </Button>
              </div>

              {!todayWorkout || todayWorkout.exercises?.length === 0 ? (
                <div className="no-workout">
                  <span className="no-workout-icon">😴</span>
                  <h3>Rest Day</h3>
                  <p>No workout scheduled for today. Take it easy and recover!</p>
                </div>
              ) : (
                <div className="workout-content">
                  <div className="exercises-grid">
                    {todayWorkout.exercises.map((exercise, index) => {
                      const isCompleted = completedExercises.includes(index);
                      const calculatedWeight = calculateWeight(exercise);

                      return (
                        <div
                          key={index}
                          className={`exercise-card ${isCompleted ? 'completed' : ''}`}
                        >
                          <div className="exercise-card-header">
                            <span className="exercise-number">{index + 1}</span>
                            <span className="exercise-name">{exercise.exerciseName}</span>
                            {exercise.youtubeUrl && (
                              <a
                                href={exercise.youtubeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="video-link-small"
                                title="Watch Demo"
                              >
                                📹
                              </a>
                            )}
                          </div>
                          <div className="exercise-card-body">
                            <div className="exercise-prescription-large">
                              <span className="sets-reps">{exercise.sets} x {exercise.reps}</span>
                              {exercise.percentage && (
                                <span className="percentage">@ {exercise.percentage}%</span>
                              )}
                              {calculatedWeight && (
                                <span className="weight">{calculatedWeight} lbs</span>
                              )}
                            </div>
                            {exercise.notes && (
                              <p className="exercise-notes-small">{exercise.notes}</p>
                            )}
                          </div>
                          {isCompleted && (
                            <div className="exercise-card-completed">
                              ✓ Done
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {completedExercises.length === todayWorkout.exercises.length && (
                    <div className="workout-complete">
                      <span className="complete-icon">🎉</span>
                      <h3>Workout Complete!</h3>
                      <p>Great job finishing today's workout!</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </section>

          {/* Stats Section - Bottom */}
          <section className="stats-section">
            <Card>
              <div className="section-header">
                <h2>Your Stats</h2>
                <Button variant="outline" size="sm" href="/athlete/stats">
                  View All Stats
                </Button>
              </div>

              <div className="stats-grid">
                <div className="stat-card primary">
                  <span className="stat-icon">🏋️</span>
                  <div className="stat-info">
                    <span className="stat-value">{stats?.workoutsCompleted || 0}</span>
                    <span className="stat-label">Workouts Completed</span>
                  </div>
                </div>

                <div className="stat-card">
                  <span className="stat-icon">📅</span>
                  <div className="stat-info">
                    <span className="stat-value">{stats?.currentStreak || 0}</span>
                    <span className="stat-label">Day Streak</span>
                  </div>
                </div>

                <div className="stat-card">
                  <span className="stat-icon">💪</span>
                  <div className="stat-info">
                    <span className="stat-value">{stats?.totalSets || 0}</span>
                    <span className="stat-label">Total Sets</span>
                  </div>
                </div>

                <div className="stat-card">
                  <span className="stat-icon">⚡</span>
                  <div className="stat-info">
                    <span className="stat-value">{stats?.totalVolume || 0}</span>
                    <span className="stat-label">Total Volume (lbs)</span>
                  </div>
                </div>
              </div>

              {stats?.oneRepMaxes && Object.keys(stats.oneRepMaxes).length > 0 && (
                <div className="one-rm-scroll-section">
                  <h3>One Rep Maxes</h3>
                  <div className="one-rm-scroll-container">
                    {Object.entries(stats.oneRepMaxes).map(([exercise, value]) => (
                      <div key={exercise} className="one-rm-scroll-item">
                        <span className="one-rm-value">{value}</span>
                        <span className="one-rm-unit">lbs</span>
                        <span className="one-rm-name">{exercise}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AthleteDashboard;
